import { BrandLockup } from "../components/BrandLockup";
import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, LogIn } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import QRCode from "qrcode";

export default function AdminLogin() {
  const { user, login, verifyMfa, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [method, setMethod] = useState("totp");
  const [emailSent, setEmailSent] = useState(null);
  const [qr, setQr] = useState("");
  const reenrol = new URLSearchParams(window.location.search).get("reenrol") === "1";
  useEffect(() => { if (mfa?.otpauth_uri) QRCode.toDataURL(mfa.otpauth_uri, { margin: 1, width: 200 }).then(setQr).catch((e) => { console.error("QR render failed", e); setQr(""); }); else setQr(""); }, [mfa]);
  const sendEmailCode = async () => {
    setErr(""); setBusy(true);
    try { const { data } = await api.post("/auth/mfa/email-code", { mfa_token: mfa.mfa_token }); setEmailSent(data); setMethod("email"); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  useEffect(() => { document.title = "Staff Login | Dutsen Kauna Foundation"; }, []);

  if (loading) return <div className="min-h-screen bg-[#155e3f]" />;
  if (user && user.role) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mfa) {
        const u = await verifyMfa(mfa.mfa_token, code, method);
        if (u?.enrol) { setMfa(u.enrol); setCode(""); setMethod("totp"); setEmailSent(null); return; }
        nav(u?.must_change_password ? "/admin/change-password" : "/admin");
        return;
      }
      const data = await login(email, password);
      setMfa(data);
      if (data.verify_email_first) { setMethod("email"); setEmailSent(data.sent ? data : null); if (!data.sent && data.detail) setErr(data.detail); }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#155e3f] flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <BrandLockup tone="dark" size="lg" className="justify-center w-full mb-4" />
          <h1 className="font-serif text-3xl text-white font-bold">Foundation Admin</h1>
          <p className="text-white text-sm mt-2">Authorised staff only. Access is role-based and every action is logged.</p>
        </div>
        <form onSubmit={onSubmit} className="bg-[#155e3f] border border-white/10 rounded-2xl p-8 space-y-4" data-testid="admin-login-form">
          {mfa ? (
            <div className="space-y-4" data-testid="admin-mfa-step">
              {mfa.enrol && mfa.verify_email_first && (
                <div className="bg-[#155e3f] rounded-xl p-4" data-testid="admin-mfa-verify-email">
                  <div className="text-[#d6aa43] text-xs uppercase tracking-widest font-bold">Confirm it's you</div>
                  <p className="text-white text-xs mt-2">Before we set up your authenticator app, enter the 6-digit code we emailed to <b className="text-white">{mfa.to}</b>. It expires in 10 minutes.</p>
                  <button type="button" onClick={sendEmailCode} disabled={busy} className="mt-3 text-xs font-bold text-[#d6aa43] underline" data-testid="admin-mfa-resend-email">Resend code</button>
                </div>
              )}
              {mfa.enrol && mfa.otpauth_uri && (
                <div className="bg-[#155e3f] rounded-xl p-4 text-center" data-testid="admin-mfa-enrol">
                  <div className="text-[#d6aa43] text-xs uppercase tracking-widest font-bold">Set up multi-factor authentication</div>
                  <p className="text-white text-xs mt-2">Scan this QR code with Google Authenticator, Microsoft Authenticator or Authy, then enter the 6-digit code. MFA is required for all staff.</p>
                  {qr ? <img alt="MFA QR code" className="mx-auto mt-3 rounded bg-white p-2" src={qr} data-testid="admin-mfa-qr" /> : <div className="mt-3 text-xs text-white">Generating QR…</div>}
                  <div className="text-xs text-white mt-3">Can't scan? In your authenticator choose <b className="text-white">Enter a setup key</b> and type:</div><div className="font-mono text-sm text-white select-all break-all mt-1 bg-white/5 rounded p-2" data-testid="admin-mfa-secret">{mfa.secret}</div><div className="text-[10px] text-white mt-1">Account: {email} · Type: time-based</div>
                </div>
              )}
              {!mfa.enrol && (
                <div className="flex rounded-lg overflow-hidden border border-white/15 text-xs font-bold" data-testid="mfa-method-switch">
                  <button type="button" onClick={() => setMethod("totp")} className={`flex-1 py-2 ${method === "totp" ? "bg-[#d6aa43] text-[#0a0d12]" : "text-white"}`} data-testid="mfa-method-totp">Authenticator app</button>
                  <button type="button" onClick={sendEmailCode} disabled={busy} className={`flex-1 py-2 ${method === "email" ? "bg-[#d6aa43] text-[#0a0d12]" : "text-white"}`} data-testid="mfa-method-email">{emailSent ? "Resend email code" : "Email me a code"}</button>
                </div>
              )}
              {method === "email" && emailSent && !mfa.verify_email_first && <p className="text-xs text-white bg-[#155e3f] rounded-md p-3" data-testid="mfa-email-sent">A 6-digit code was sent to <b className="text-white">{emailSent.to}</b>. It expires in 10 minutes.</p>}
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-white font-semibold mb-2 block">{method === "email" ? "Code from your email" : "Code from your authenticator app"}</label>
                <input inputMode="numeric" autoFocus required value={code} onChange={(e) => setCode(e.target.value)} placeholder="123 456" className="w-full bg-[#155e3f] border border-white/15 rounded-md px-4 py-3 text-white font-mono text-xl tracking-widest text-center focus:outline-none focus:border-[#d6aa43]" data-testid="admin-mfa-code" />
              </div>
              <button type="button" onClick={() => { setMfa(null); setCode(""); setMethod("totp"); setEmailSent(null); }} className="text-xs text-white underline">Back</button>
            </div>
          ) : (<>
              {reenrol && <p className="text-xs text-[#d6aa43] bg-[#155e3f] rounded-md p-3" data-testid="reenrol-banner">Your authenticator was reset. Sign in with your password — we'll email you a confirmation code, then show a new QR code to scan.</p>}
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-white font-semibold mb-2 block">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#155e3f] border border-white/15 rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#d6aa43]" data-testid="admin-email" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-white font-semibold mb-2 block">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-[#155e3f] border border-white/15 rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#d6aa43]" data-testid="admin-password" />
          </div>
          </>)}
          {err && (
            <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="admin-error">
              {err}
            </div>
          )}
          <button type="submit" disabled={busy} className="dk-btn-primary w-full justify-center disabled:opacity-60" data-testid="admin-login-submit">
            <LogIn size={16} /> {busy ? "Verifying..." : mfa ? "Verify code" : "Continue"}
          </button>
        </form>
        <div className="text-center mt-6 text-xs text-white/90">
          <Lock size={12} className="inline mr-1" /> Secured by JWT
        </div>
      </div>
    </div>
  );
}
